import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  AmenitiesGrid as BaseAmenitiesGrid,
  contentSchema as baseSchema,
} from '@vibesites/theme-via6/components/AmenitiesGrid';

/**
 * The Majestic — AmenitiesGrid override (home-page icon grid).
 *
 * The parent's amenity tiles are `aspect-square` boxes but ship with a
 * fairly small 72px icon (56px on phones) and `text-xs sm:text-sm`
 * labels, leaving a lot of empty space inside each box. Per customer
 * request we enlarge both the icon glyph and the label text so the
 * tiles read as fuller / more balanced.
 *
 * Both targets live inside the parent's opaque JSX tree, so a CSS
 * style wrapper is the cheapest override that reaches them (no need to
 * fork the whole component + its ten slot definitions):
 *
 *   1. `.amenities-grid-icon-wrap` — the parent's stable icon-wrapper
 *      class (the masked SVG fills it at 100%). Bumped from 72/56px to
 *      112/80px.
 *   2. The per-tile label — the last child of each `.aspect-square`
 *      tile (the icon slot group is the first child). Bumped from
 *      ~12–14px to ~1.25rem, with a touch more top margin.
 *
 * Everything is scoped to `[data-vibesite-slot="AmenitiesGrid"]` — the
 * stable data attribute the editable section wrapper carries — so the
 * rules only affect this section and can't leak elsewhere. The scoped
 * selectors also out-specify the parent's single-class Tailwind
 * utilities, so no `!important` is needed.
 *
 * The base schema travels with us via the re-attach below — without it
 * the editor would show no editing affordances on the wrapper.
 */
export function AmenitiesGrid() {
  return (
    <>
      <BaseAmenitiesGrid />
      <style>{`
        /* Remove two amenity tiles per customer request. The parent
           renders a fixed 8-tile grid whose tiles are the only direct
           children of the grid container (each is an \`.aspect-square\`),
           so \`:nth-child\` positions map 1:1 to the parent's tile order:
             - tile 1 = "Scenic Swimming Pool with Spa" (swimming pool)
             - tile 8 = "Walk-In Closets*"
           display:none pulls them out of the CSS-grid flow entirely, so
           the remaining six tiles reflow with no empty cell or leftover
           gap background. The parent's slot definitions aren't exported,
           so hiding here is the cheapest way to drop the tiles without
           forking the whole component + its ten slot definitions. */
        [data-vibesite-slot="AmenitiesGrid"] .aspect-square:nth-child(1),
        [data-vibesite-slot="AmenitiesGrid"] .aspect-square:nth-child(8) {
          display: none;
        }
        /* With two tiles removed, six remain. The parent's grid is
           4 columns at >=lg (\`lg:grid-cols-4\`), so six tiles fill the
           first row (4) and leave two empty cells on the second row —
           these render as white gaps (the grid's \`gap-px\` background).
           Switch to 3 columns at >=lg so the six tiles form a clean
           3x2 block with no empty cells. We target the tile grid via
           \`:has(> .aspect-square)\` (the heading grid above has no such
           children). Below lg the parent already uses 2 columns, where
           six tiles fill 3x2 evenly, so no override is needed there. */
        @media (min-width: 1024px) {
          [data-vibesite-slot="AmenitiesGrid"] .grid:has(> .aspect-square) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        /* Each tile fills its full grid-cell height. The parent renders
           tiles as \`aspect-square\`, which pins a tile's height to its
           width. CSS grid still makes every cell in a row as tall as the
           tallest tile, so when a row-mate's label wraps to more lines
           than fit in the square, that cell grows but the square tile
           stays short — revealing the grid's light gap background
           (\`gap-px bg-[var(--vibe-color-text-on-primary)]\`) as a white
           bar/block below the shorter tile. Enlarging the labels made
           this show up. height:100% lets every tile stretch to the row
           height (the square still acts as the minimum), so no gap
           shows through. */
        [data-vibesite-slot="AmenitiesGrid"] .aspect-square {
          height: 100%;
        }
        [data-vibesite-slot="AmenitiesGrid"] .amenities-grid-icon-wrap {
          width: 112px;
          height: 112px;
        }
        /* Tile label = last child of the aspect-square tile (the icon
           slot group is the first child). */
        [data-vibesite-slot="AmenitiesGrid"] .aspect-square > :last-child {
          font-size: 1.25rem;
          line-height: 1.4;
          margin-top: 1.25rem;
        }
        @media (max-width: 640px) {
          /* On phones the grid is only 2 columns, so each tile is
             narrow and a strict \`aspect-square\` can't contain the
             longer labels (e.g. "Gourmet Kitchens w/ Energy-Efficient
             Appliances"). The text overflowed past the tile's bottom
             edge, colliding with the row below. Drop the square + the
             desktop \`height:100%\` on phones and let each tile size to
             its content, with a \`min-height\` floor so short tiles still
             read as roomy cards. The grid's default
             \`align-items:stretch\` keeps every tile in a row the same
             height, so no gap background shows through. */
          [data-vibesite-slot="AmenitiesGrid"] .aspect-square {
            aspect-ratio: auto;
            height: auto;
            min-height: 13rem;
          }
          [data-vibesite-slot="AmenitiesGrid"] .amenities-grid-icon-wrap {
            width: 80px;
            height: 80px;
          }
          [data-vibesite-slot="AmenitiesGrid"] .aspect-square > :last-child {
            font-size: 1.05rem;
          }
        }
      `}</style>
    </>
  );
}

(AmenitiesGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  baseSchema;

export default AmenitiesGrid;
